import Phaser from 'phaser';

/**
 * Backdrop visual compartilhado entre cenas.
 *
 * Pinta um gradiente vertical escuro + uma constelação de estrelas
 * determinísticas (mesmas posições em toda cena, evitando o "ruído
 * trêmulo" entre transições). É chamado no início de `create()` de cada
 * cena e fica em depth -1000 — qualquer outro objeto renderiza por cima.
 *
 * Não é uma cena à parte por dois motivos: (1) custo zero comparado a
 * uma BackdropScene paralela, (2) cada cena pode ajustar a tonalidade do
 * accent passando uma cor — útil para mundos com paleta diferente.
 */

export interface BackdropOptions {
  /** Cor do "halo" no topo da tela (0xRRGGBB). Default: roxo escuro. */
  accentColor?: number;
}

const STAR_COUNT = 80;
const BACKDROP_DEPTH = -1000;

/** Gerador determinístico — mantém estrelas estáveis entre cenas. */
function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function addBackdrop(scene: Phaser.Scene, options: BackdropOptions = {}): void {
  const { width, height } = scene.scale;
  const accent = options.accentColor ?? 0x1f1745;

  const bg = scene.add.graphics();
  bg.setDepth(BACKDROP_DEPTH);

  // Gradiente vertical: topo com toque do accent, base mais escura.
  // fillGradientStyle aceita 4 cantos (TL, TR, BL, BR).
  bg.fillGradientStyle(accent, accent, 0x070716, 0x070716, 1);
  bg.fillRect(0, 0, width, height);

  // Estrelas: posições + tamanhos determinísticos via seed fixo, mas
  // cada estrela tem brilho leve para criar profundidade.
  const rand = mulberry32(2026); // seed escolhida arbitrariamente, ano do jogo
  for (let i = 0; i < STAR_COUNT; i++) {
    const x = rand() * width;
    const y = rand() * height;
    const radius = 0.6 + rand() * 1.6;
    const alpha = 0.25 + rand() * 0.55;
    bg.fillStyle(0xffffff, alpha);
    bg.fillCircle(x, y, radius);
  }
}
